import { useState, useEffect } from "react";
import { 
  X, MessageCircle, Edit2, Calendar, FileText, Receipt, 
  FileSignature, Send, Plus, Download, Check, Loader2,
  AlertTriangle, ChevronRight, DollarSign, Clock, MapPin,
  User, Mail, Phone, Tag
} from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { Separator } from "./ui/separator";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

// Import existing components
import MessageThread from "./MessageThread";
import QuoteBuilder from "./QuoteBuilder";
import AddToCalendar from "./AddToCalendar";

const BookingDetailModal = ({ 
  open, 
  onOpenChange, 
  request, 
  onRefresh,
  onEditBooking,
  onAttachContracts,
  onStatusChange
}) => {
  const [activeTab, setActiveTab] = useState("quote");
  const [loading, setLoading] = useState(false);
  
  // Quote state
  const [quote, setQuote] = useState(null);
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  
  // Invoices & Charges state
  const [invoices, setInvoices] = useState([]);
  const [adHocCharges, setAdHocCharges] = useState([]);
  
  // Contracts state
  const [contracts, setContracts] = useState([]);
  const [availableContracts, setAvailableContracts] = useState([]);
  
  // Message dialog state
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  
  // Financial summary
  const [financialSummary, setFinancialSummary] = useState({
    total: 0,
    depositPaid: 0,
    additionalCharges: 0,
    balanceDue: 0
  });
  
  // Action loading states
  const [sendingPayment, setSendingPayment] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(null);

  useEffect(() => {
    if (open && request) {
      fetchBookingDetails();
    }
  }, [open, request?.request_id]);

  const fetchBookingDetails = async () => {
    if (!request) return;
    setLoading(true);
    
    try {
      // Fetch all related data in parallel
      const [quoteRes, contractRes] = await Promise.allSettled([
        axios.get(`${API}/quotes/booking/${request.request_id}`, { withCredentials: true }),
        axios.get(`${API}/contracts/booking/${request.request_id}`, { withCredentials: true })
      ]);
      
      // Process quote
      if (quoteRes.status === "fulfilled") {
        setQuote(quoteRes.value.data);
        calculateFinancialSummary(quoteRes.value.data);
      } else {
        setQuote(null);
      }
      
      // Process contracts
      if (contractRes.status === "fulfilled" && contractRes.value.data?.contracts?.length > 0) {
        setContracts(contractRes.value.data.contracts);
      } else {
        setContracts([]);
      }
      
      // Fetch ad-hoc charges/invoices for this booking
      try {
        const invoicesRes = await axios.get(
          `${API}/bookings/request/${request.request_id}/invoice`,
          { withCredentials: true }
        );
        if (invoicesRes.data) {
          setInvoices([invoicesRes.data]);
        }
      } catch (err) {
        setInvoices([]);
      }
      
    } catch (error) {
      console.error("Failed to fetch booking details:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateFinancialSummary = (quoteData) => {
    if (!quoteData) {
      setFinancialSummary({ total: 0, depositPaid: 0, additionalCharges: 0, balanceDue: 0 });
      return;
    }
    
    const total = quoteData.total || 0;
    const depositPaid = request?.deposit_paid ? (quoteData.deposit_amount || 0) : 0;
    const additionalCharges = adHocCharges.reduce((sum, charge) => sum + (charge.amount || 0), 0);
    const balanceDue = total + additionalCharges - depositPaid;
    
    setFinancialSummary({
      total,
      depositPaid,
      additionalCharges,
      balanceDue: Math.max(0, balanceDue)
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      confirmed: "bg-green-500/20 text-green-400 border-green-500/30",
      declined: "bg-red-500/20 text-red-400 border-red-500/30",
      needs_info: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      completed: "bg-purple-500/20 text-purple-400 border-purple-500/30",
      in_discussion: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      quote_sent: "bg-orange-500/20 text-orange-400 border-orange-500/30",
      quote_declined: "bg-red-500/20 text-red-400 border-red-500/30",
      deposit_paid: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
      event_completed: "bg-indigo-500/20 text-indigo-400 border-indigo-500/30"
    };
    return styles[status] || styles.pending;
  };

  const sendFinalPaymentRequest = async () => {
    setSendingPayment(true);
    try {
      await axios.post(
        `${API}/bookings/request/${request.request_id}/send-final-payment`,
        {},
        { withCredentials: true }
      );
      toast.success("Final payment request sent to client!");
      onRefresh?.();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to send payment request";
      toast.error(errorMsg);
    } finally {
      setSendingPayment(false);
    }
  };

  const downloadQuotePdf = async () => {
    setDownloadingPdf("quote");
    try {
      const response = await axios.get(
        `${API}/quotes/booking/${request.request_id}/pdf`,
        { withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `quote_${request.request_id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Quote PDF downloaded");
    } catch (error) {
      toast.error("Failed to download quote PDF");
    } finally {
      setDownloadingPdf(null);
    }
  };

  const downloadInvoicePdf = async () => {
    setDownloadingPdf("invoice");
    try {
      const response = await axios.get(
        `${API}/bookings/request/${request.request_id}/invoice-pdf`,
        { withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${request.request_id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Invoice PDF downloaded");
    } catch (error) {
      toast.error("Failed to download invoice PDF");
    } finally {
      setDownloadingPdf(null);
    }
  };

  const handleQuoteSent = () => {
    setQuoteDialogOpen(false);
    fetchBookingDetails();
    onRefresh?.();
  };

  // Handle quote saved (draft) - update UI immediately without closing modal
  const handleQuoteSaved = async () => {
    // Re-fetch to get full quote data - don't set partial data from response
    // This prevents render crashes from incomplete quote objects
    try {
      await fetchBookingDetails();
      onRefresh?.();
    } catch (error) {
      console.error("Error refreshing after quote save:", error);
    }
  };

  const openAddCharge = () => {
    // Redirect to quotes-invoices page with booking pre-selected
    window.location.href = `/dashboard/quotes-invoices?booking=${request.request_id}&name=${encodeURIComponent(request.customer_name)}&email=${encodeURIComponent(request.customer_email)}`;
  };

  if (!request) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-full max-w-4xl h-[100dvh] sm:h-auto sm:max-h-[90vh] p-0 overflow-hidden rounded-none sm:rounded-lg" data-testid="booking-detail-modal">
          {/* Fixed Header */}
          <div className="sticky top-0 z-10 bg-card border-b border-border p-4 sm:p-6 relative">
            <div className="flex items-start justify-between gap-2 sm:gap-4 pr-10 sm:pr-0">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                  <h2 className="text-xl sm:text-2xl font-serif truncate max-w-[200px] sm:max-w-none">{request.customer_name}</h2>
                  <Badge className={`${getStatusBadge(request.status)} border shrink-0 text-xs`}>
                    {request.status?.replace(/_/g, " ")}
                  </Badge>
                  {request.google_calendar_event_id && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Calendar className="w-4 h-4 text-green-500 shrink-0" />
                        </TooltipTrigger>
                        <TooltipContent>Synced to Google Calendar</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
                
                <div className="flex flex-wrap items-center gap-x-3 sm:gap-x-4 gap-y-1 text-xs sm:text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Tag className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" />
                    <span className="truncate">{request.event_type}</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" />
                    <span className="truncate">
                      {request.is_tournament 
                        ? `${request.tournament_start_date} - ${request.tournament_end_date}`
                        : request.event_date || "Date TBD"}
                    </span>
                  </span>
                  {request.start_time && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" />
                      <span>{request.start_time} - {request.end_time}</span>
                    </span>
                  )}
                </div>

                {/* Venue on its own line for mobile */}
                {(request.venue_name || request.venue_address) && (
                  <div className="flex items-center gap-1 text-xs sm:text-sm text-muted-foreground mt-1">
                    <MapPin className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" />
                    <span className="truncate">{request.venue_name || request.venue_address}</span>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-x-3 sm:gap-x-4 gap-y-1 text-xs sm:text-sm text-muted-foreground mt-1 sm:mt-2">
                  <span className="flex items-center gap-1">
                    <Mail className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" />
                    <span className="truncate max-w-[180px] sm:max-w-none">{request.customer_email}</span>
                  </span>
                  {request.customer_phone && (
                    <span className="flex items-center gap-1">
                      <Phone className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" />
                      <span>{request.customer_phone}</span>
                    </span>
                  )}
                </div>
              </div>
              
              {/* Close Button - More prominent on mobile */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onOpenChange(false)}
                className="shrink-0 h-9 w-9 sm:h-8 sm:w-8 rounded-full hover:bg-muted absolute top-3 right-3 sm:relative sm:top-auto sm:right-auto"
                aria-label="Close"
              >
                <X className="w-5 h-5 sm:w-4 sm:h-4" />
              </Button>
            </div>

            {/* Financial Summary Bar - 2x2 grid on mobile, 4 cols on desktop */}
            {quote && (
              <div className="mt-3 sm:mt-4 p-2 sm:p-3 bg-muted/50 rounded-lg">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 text-center">
                  <div>
                    <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider">Total</p>
                    <p className="text-base sm:text-lg font-semibold">${financialSummary.total.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider">Deposit</p>
                    <p className="text-base sm:text-lg font-semibold text-green-500">
                      ${financialSummary.depositPaid.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider">Additional</p>
                    <p className="text-base sm:text-lg font-semibold">
                      ${financialSummary.additionalCharges.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider">Balance</p>
                    <p className={`text-base sm:text-lg font-semibold ${financialSummary.balanceDue > 0 ? 'text-orange-500' : 'text-green-500'}`}>
                      ${financialSummary.balanceDue.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6" style={{ maxHeight: 'calc(100dvh - 280px)' }}>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="w-full justify-start mb-4 sm:mb-6">
                  <TabsTrigger value="quote" className="flex-1 text-xs sm:text-sm">
                    <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    Quote
                  </TabsTrigger>
                  <TabsTrigger value="invoices" className="flex-1 text-xs sm:text-sm">
                    <Receipt className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    Invoices
                  </TabsTrigger>
                  <TabsTrigger value="contracts" className="flex-1 text-xs sm:text-sm">
                    <FileSignature className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    Contracts
                    {contracts.length > 0 && (
                      <Badge variant="secondary" className="ml-1 sm:ml-2 text-[10px] sm:text-xs">{contracts.length}</Badge>
                    )}
                  </TabsTrigger>
                </TabsList>

                {/* Quote Tab */}
                <TabsContent value="quote" className="space-y-4">
                  {quote ? (
                    <div className="space-y-4 sm:space-y-6">
                      {/* Quote Details */}
                      <div className="border rounded-lg p-3 sm:p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                          <div>
                            <h3 className="font-semibold text-base sm:text-lg">{quote.package_name}</h3>
                            {quote.package_description && (
                              <p className="text-sm text-muted-foreground">{quote.package_description}</p>
                            )}
                          </div>
                          <Badge variant={quote.status === "accepted" ? "default" : "secondary"}>
                            {quote.status || "Draft"}
                          </Badge>
                        </div>

                        {/* Line Items */}
                        {quote.line_items?.length > 0 && (
                          <div className="space-y-2 mb-4">
                            <p className="text-xs uppercase tracking-wider text-muted-foreground">Items</p>
                            {quote.line_items.map((item, idx) => (
                              <div key={idx} className="flex justify-between text-sm">
                                <span>{item.description} × {item.quantity}</span>
                                <span>${(item.quantity * item.unit_price).toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Totals */}
                        <Separator className="my-4" />
                        <div className="space-y-2">
                          {quote.travel_fee > 0 && (
                            <div className="flex justify-between text-sm">
                              <span>Travel Fee</span>
                              <span>${quote.travel_fee.toFixed(2)}</span>
                            </div>
                          )}
                          {quote.discount > 0 && (
                            <div className="flex justify-between text-sm text-green-500">
                              <span>Discount</span>
                              <span>-${quote.discount.toFixed(2)}</span>
                            </div>
                          )}
                          {quote.tax_rate > 0 && (
                            <div className="flex justify-between text-sm">
                              <span>Tax ({quote.tax_rate}%)</span>
                              <span>${((quote.total || 0) * quote.tax_rate / 100).toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between font-semibold pt-2 border-t">
                            <span>Total</span>
                            <span>${(quote.total || 0).toFixed(2)}</span>
                          </div>
                        </div>

                        {/* Deliverables */}
                        {quote.deliverables && (
                          <div className="mt-4 pt-4 border-t">
                            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Deliverables</p>
                            <p className="text-sm">{quote.deliverables}</p>
                          </div>
                        )}
                      </div>

                      {/* Quote Actions */}
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => setQuoteDialogOpen(true)}>
                          <Edit2 className="w-4 h-4 mr-2" />
                          Edit Quote
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={downloadQuotePdf}
                          disabled={downloadingPdf === "quote"}
                        >
                          {downloadingPdf === "quote" ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4 mr-2" />
                          )}
                          Download PDF
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 border rounded-lg border-dashed">
                      <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                      <p className="text-lg font-medium mb-2">No Quote Yet</p>
                      <p className="text-muted-foreground mb-4">Create a quote to send pricing to your client</p>
                      <Button onClick={() => setQuoteDialogOpen(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Create Quote
                      </Button>
                    </div>
                  )}
                </TabsContent>

                {/* Invoices Tab */}
                <TabsContent value="invoices" className="space-y-6">
                  {/* Invoices Section */}
                  <div>
                    <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                      <Receipt className="w-4 h-4" />
                      Invoices
                    </h4>
                    {invoices.length > 0 ? (
                      <div className="space-y-3">
                        {invoices.map((invoice, idx) => (
                          <div key={idx} className="border rounded-lg p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">{invoice.package_name || "Invoice"}</p>
                                <p className="text-sm text-muted-foreground">
                                  Due: {invoice.due_date || "Upon receipt"}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="font-semibold">${invoice.total?.toFixed(2) || "0.00"}</p>
                                <Badge variant={invoice.status === "paid" ? "default" : "secondary"}>
                                  {invoice.status || "pending"}
                                </Badge>
                              </div>
                            </div>
                            <div className="flex gap-2 mt-3">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={downloadInvoicePdf}
                                disabled={downloadingPdf === "invoice"}
                              >
                                {downloadingPdf === "invoice" ? (
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                  <Download className="w-4 h-4 mr-2" />
                                )}
                                Download
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 border rounded-lg border-dashed">
                        <Receipt className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-50" />
                        <p className="text-muted-foreground">No invoices generated yet</p>
                      </div>
                    )}
                  </div>

                  <Separator />

                  {/* Ad-Hoc Charges Section */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium flex items-center gap-2">
                        <DollarSign className="w-4 h-4" />
                        Ad-Hoc Charges
                      </h4>
                      <Button variant="outline" size="sm" onClick={openAddCharge}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add Charge
                      </Button>
                    </div>
                    {adHocCharges.length > 0 ? (
                      <div className="space-y-2">
                        {adHocCharges.map((charge, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                            <div>
                              <p className="font-medium">{charge.description}</p>
                              <p className="text-sm text-muted-foreground">{charge.date}</p>
                            </div>
                            <p className="font-semibold">${charge.amount?.toFixed(2)}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 border rounded-lg border-dashed">
                        <DollarSign className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-50" />
                        <p className="text-muted-foreground">No additional charges</p>
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Contracts Tab */}
                <TabsContent value="contracts" className="space-y-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium flex items-center gap-2">
                      <FileSignature className="w-4 h-4" />
                      Attached Contracts
                    </h4>
                    <Button variant="outline" size="sm" onClick={() => onAttachContracts?.(request)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Attach Contract
                    </Button>
                  </div>
                  
                  {contracts.length > 0 ? (
                    <div className="space-y-3">
                      {contracts.map((contract, idx) => (
                        <div key={idx} className="border rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <FileSignature className="w-5 h-5 text-muted-foreground" />
                              <div>
                                <p className="font-medium">{contract.template_name}</p>
                                <p className="text-sm text-muted-foreground">
                                  {contract.client_name} • {new Date(contract.created_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <Badge 
                              variant="outline" 
                              className={contract.status === "signed" 
                                ? "text-green-400 border-green-400/30" 
                                : "text-yellow-400 border-yellow-400/30"
                              }
                            >
                              {contract.status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 border rounded-lg border-dashed">
                      <FileSignature className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                      <p className="text-lg font-medium mb-2">No Contracts Attached</p>
                      <p className="text-muted-foreground mb-4">Attach contracts for this booking</p>
                      <Button variant="outline" onClick={() => onAttachContracts?.(request)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Attach Contract
                      </Button>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            )}
          </div>

          {/* Fixed Bottom Action Bar - Scrollable on mobile */}
          <div className="sticky bottom-0 bg-card border-t border-border p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3 overflow-x-auto pb-1 sm:pb-0 -mx-1 px-1 scrollbar-hide">
              {/* Left side - always visible actions */}
              <Button variant="outline" size="sm" onClick={() => onEditBooking?.(request)} className="shrink-0">
                <Edit2 className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Edit Booking</span>
              </Button>
              <Button variant="outline" size="sm" onClick={() => setMessageDialogOpen(true)} className="shrink-0">
                <MessageCircle className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Message</span>
              </Button>
              {request.status === "confirmed" && (
                <div className="shrink-0">
                  <AddToCalendar
                    title={`📸 ${request.event_type}: ${request.customer_name}`}
                    description={`Client: ${request.customer_name}\nEmail: ${request.customer_email}\nPhone: ${request.customer_phone || 'N/A'}\nType: ${request.event_type}\nServices: ${request.request_types?.join(', ')}\n\nNotes: ${request.notes || 'None'}`}
                    location={request.venue_name ? `${request.venue_name}${request.venue_address ? ', ' + request.venue_address : ''}` : request.venue_address || ''}
                    startDate={request.event_date}
                    startTime={request.start_time || "09:00"}
                    endTime={request.end_time || "17:00"}
                  />
                </div>
              )}

              {/* Divider on desktop */}
              <div className="hidden sm:block w-px h-6 bg-border mx-1" />

              {/* Right side - context-specific actions */}
              {/* Show Create Quote only if no quote exists */}
              {!quote && (request.status === "pending" || request.status === "in_discussion") && (
                <Button size="sm" onClick={() => setQuoteDialogOpen(true)} className="shrink-0" data-testid="create-quote-btn">
                  <FileText className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Create Quote</span>
                </Button>
              )}
              
              {/* Show View/Edit Quote button when quote exists */}
              {quote && (
                <Button 
                  size="sm" 
                  variant={quote.status === "draft" ? "default" : "outline"}
                  onClick={() => setQuoteDialogOpen(true)} 
                  className="shrink-0" 
                  data-testid="edit-quote-btn"
                >
                  <FileText className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">
                    {quote.status === "draft" ? "Edit Draft" : "View Quote"}
                  </span>
                </Button>
              )}

              {/* Pending status actions */}
              {request.status === "pending" && (
                <>
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 shrink-0"
                    onClick={() => onStatusChange?.(request, "confirmed")}
                  >
                    <Check className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Confirm</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-400 hover:text-red-300 shrink-0"
                    onClick={() => onStatusChange?.(request, "declined")}
                  >
                    <X className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Decline</span>
                  </Button>
                </>
              )}

              {/* Confirmed status actions */}
              {request.status === "confirmed" && (
                <>
                  {!request.final_payment_paid && request.deposit_paid && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={sendFinalPaymentRequest}
                      disabled={sendingPayment}
                      className="shrink-0"
                    >
                      <Send className="w-4 h-4 sm:mr-2" />
                      <span className="hidden sm:inline">{sendingPayment ? "Sending..." : "Final Payment"}</span>
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onStatusChange?.(request, "completed")}
                    className="shrink-0"
                  >
                    <Check className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Complete</span>
                  </Button>
                </>
              )}

              {/* Quote sent/declined - allow requoting or declining */}
              {(request.status === "quote_sent" || request.status === "quote_declined" || request.status === "in_discussion") && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-400 hover:text-red-300 shrink-0"
                  onClick={() => onStatusChange?.(request, "declined")}
                >
                  <X className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Decline</span>
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Message Dialog */}
      <Dialog open={messageDialogOpen} onOpenChange={setMessageDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              Conversation with {request?.customer_name}
            </DialogTitle>
            <DialogDescription>
              {request?.event_type} • {request?.event_date || "Date TBD"}
            </DialogDescription>
          </DialogHeader>
          
          {request && (
            <MessageThread 
              bookingId={request.request_id}
              onMessageSent={() => {
                onRefresh?.();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Quote Builder */}
      <QuoteBuilder
        bookingId={request?.request_id}
        booking={request}
        open={quoteDialogOpen}
        onOpenChange={setQuoteDialogOpen}
        existingQuote={quote}
        onQuoteSent={handleQuoteSent}
        onQuoteSaved={handleQuoteSaved}
      />
    </>
  );
};

export default BookingDetailModal;
